/**
 * FieldToolsSettingsPage — super-admin-only page to configure the
 * Google Maps Geocoding API key used by the "Find customers near me"
 * feature. The key is stored in the Mongo ``field_tools_settings``
 * collection and read by ``field_tools_routes._google_geocode``.
 */
import { useEffect, useState } from "react";
import { Key, Eye, EyeSlash, FloppyDisk, ArrowsClockwise, CheckCircle, X, MapPin, Trash, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminPageHeader from "@/components/AdminPageHeader";

export default function FieldToolsSettingsPage({ user, onLogout, onLogoutAll }) {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({ present: false, masked: "", updated_by: "" });
  const [keyInput, setKeyInput] = useState("");
  const [reveal, setReveal] = useState(false);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/field-tools/settings");
      setState({
        present: !!r.data.google_geocode_api_key_present,
        masked: r.data.google_geocode_api_key_masked || "",
        updated_by: r.data.updated_by || "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load settings");
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    try {
      await api.put("/admin/field-tools/settings", { google_geocode_api_key: keyInput.trim() });
      toast.success(keyInput.trim() ? "Geocoding API key saved." : "Geocoding key cleared.");
      setKeyInput("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  async function clearKey() {
    if (!window.confirm("Clear the Google Geocoding API key? The 'Find customers near me' feature will stop working until a new key is provided.")) return;
    setSaving(true);
    try {
      await api.put("/admin/field-tools/settings", { google_geocode_api_key: "" });
      toast.success("Cleared.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="min-h-screen bg-slate-50 font-['Lato'] text-slate-900" data-testid="field-tools-settings-page">
      <AdminPageHeader sticky maxWidth="max-w-3xl" testId="field-tools-settings-back"
        icon={<MapPin size={20} weight="fill" className="text-emerald-600" />}
        title="Field tools · Settings"
        subtitle="Configure the Google Maps Geocoding key that powers nearby-customer lookup."
        user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />

      <main className="max-w-3xl mx-auto p-6 pb-32 space-y-5">
        <section className="bg-blue-50 border border-blue-200 rounded-md p-3 text-[12px] text-blue-900">
          <strong>How it works:</strong> When a dealer or main-company employee taps
          "Find customers near me", the portal calls the Google Geocoding API to convert each
          customer's address into latitude/longitude. Results are cached forever in Mongo —
          you only pay once per unique address. Free tier: $200/month ≈ 40,000 lookups.
        </section>

        <section className="bg-white border border-slate-200 rounded-md p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Key size={16} weight="bold" className="text-emerald-600" />
            <h2 className="text-sm font-bold tracking-tight">Google Maps Geocoding API key</h2>
          </div>

          {loading ? (
            <div className="py-6 text-center"><ArrowsClockwise size={20} weight="bold" className="mx-auto animate-spin text-slate-400" /></div>
          ) : (
            <>
              <div className="text-[12px] text-slate-600">
                Current: {state.present ? (
                  <span className="inline-flex items-center gap-1 text-emerald-700">
                    <CheckCircle size={11} weight="fill" /> configured
                    <span className="font-mono text-slate-700" data-testid="field-tools-settings-masked">{state.masked}</span>
                    {state.updated_by && <span className="text-slate-400 text-[11px]">· last set by {state.updated_by}</span>}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-amber-700">
                    <Warning size={11} weight="fill" /> not configured · nearby-customers feature will return 424.
                  </span>
                )}
              </div>

              <label className="block">
                <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Paste a new API key
                </span>
                <div className="flex gap-1">
                  <input type={reveal ? "text" : "password"}
                         value={keyInput}
                         onChange={(e) => setKeyInput(e.target.value)}
                         placeholder="AIzaSy..."
                         data-testid="field-tools-settings-key-input"
                         className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none font-mono" />
                  <button type="button" onClick={() => setReveal((v) => !v)}
                          title={reveal ? "Hide" : "Reveal"}
                          data-testid="field-tools-settings-toggle-reveal"
                          className="px-2 border border-slate-300 rounded-sm hover:bg-slate-100 text-slate-600">
                    {reveal ? <EyeSlash size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  Get one at <a href="https://console.cloud.google.com/google/maps-apis/credentials"
                    target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
                    console.cloud.google.com</a> → enable <em>Geocoding API</em> → restrict to it.
                </p>
              </label>

              <div className="flex flex-wrap gap-2 pt-2">
                <button onClick={save}
                        disabled={saving || !keyInput.trim()}
                        data-testid="field-tools-settings-save"
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm bg-blue-700 hover:bg-blue-800 text-white disabled:opacity-50">
                  <FloppyDisk size={12} weight="fill" /> Save key
                </button>
                {state.present && (
                  <button onClick={clearKey}
                          disabled={saving}
                          data-testid="field-tools-settings-clear"
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border border-rose-300 text-rose-700 hover:bg-rose-50">
                    <Trash size={12} weight="fill" /> Clear current key
                  </button>
                )}
              </div>
            </>
          )}
        </section>

        <section className="bg-white border border-slate-200 rounded-md p-5 space-y-2 text-[12px] text-slate-700">
          <h2 className="text-sm font-bold tracking-tight">Who can use the field-tools features?</h2>
          <ul className="list-disc list-inside space-y-1">
            <li>Anyone with an email at <code className="bg-slate-100 px-1">@usaircompressor.com</code>,
              <code className="bg-slate-100 px-1">@aircompressor.com</code>, or
              <code className="bg-slate-100 px-1">@aircompressorcanada.com</code></li>
            <li>Customer-role users whose company <code className="bg-slate-100 px-1">Customer.Type</code> is
              <em> (A) Distributor</em>, <em>ReSeller</em>, <em>Sub Dealer</em>, or <em>OEM</em>
              (i.e. dealers + sub-dealers)</li>
            <li>End-user customers do NOT see the record or nearby buttons</li>
          </ul>
        </section>
      </main>
    </div>
  );
}
